import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const waiverSchema = z.object({
  name: z.string().min(4, 'Name must be at least 4 characters')
});

type WaiverFormData = z.infer<typeof waiverSchema>;

interface WaiverFormProps {
  onComplete: () => void;
}

const WaiverForm: React.FC<WaiverFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<WaiverFormData>({
    resolver: zodResolver(waiverSchema)
  });

  const onSubmit = async (data: WaiverFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitWaiver(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit waiver form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">WebFit Waiver</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
        </div>

        <div className="prose prose-gray max-w-none mb-8">
          <p className="mb-4">
            I have volunteered to participate in a fitness program provided to me by WebFit, which may include, but may not be limited to, resistance training and aerobic or cardiovascular exercise. In consideration of WebFit's agreement to instruct and train me, I do here now and forever release and discharge and hereby hold WebFit and its respective agents, heirs, assigns, contractors, and employees from any and all claims, demands, damages, rights of action or causes of action, present or future, arising out of or connected with my participation in this or any exercise program including any injuries resulting there from.
          </p>

          <p className="mb-4 font-semibold">
            THIS WAIVER AND RELEASE OF LIABILITY INCLUDES, WITHOUT LIMITATION, INJURIES WHICH MAY OCCUR AS A RESULT OF (1) EQUIPMENT BELONGING TO THE GYM YOU ARE A PART OF THAT MAY MALFUNCTION OR BREAK; (2) AND/OR ANY SLIP, FALL, DROPPING OF EQUIPMENT.
          </p>

          <p className="mb-4">
            I have been informed of, understand and am aware that any exercise program, whether or not requiring the use of exercise equipment, is a potentially hazardous activity. I also have been informed of, understand and am aware that any exercise and/or fitness activities involve a risk of injury, as well as abnormal changes in blood pressure, fainting, and a remote risk of heart attack, stroke, other serious disability or death, and that I am voluntarily participating in these activities and using equipment and machinery with full knowledge, understanding and appreciation of the dangers involved. I hereby agree to expressly assume and accept any and all risks of injury, regardless of severity, or death.
          </p>

          <p className="mb-4">
            I understand that I am voluntarily participating in a WebFit Training program, and that no in-person physical assessment will be performed prior to the design of my program, and that my trainer will provide a fitness program based on the information that I provide. Any information that I provide that lacks detail, completeness, clarity, candor, and/or misrepresents reality in any way may result in a program containing inappropriate elements. I understand that the quality of the information that I provide is solely my responsibility, and that WebFit Trainers in no way shall be liable for any and all consequences and outcomes arising from the information provided.
          </p>

          <p className="mb-4">
            I have been advised that an examination by a physician should be obtained by anyone prior to commencing a fitness and/or exercise program, or initiating a substantial change in the amount of regular physical activity performed. If I have chosen not to obtain a physician's consent prior to beginning this fitness program with WebFit, I hereby agree that I am doing so solely at my own risk. In any event, I acknowledge and agree that I assume the risks associated with any and all fitness related activities and/or exercises in which I participate.
          </p>

          <p className="mb-4 font-semibold">
            I ACKNOWLEDGE THAT I HAVE THOROUGHLY READ THIS FORM IN ITS ENTIRETY AND FULLY UNDERSTAND THAT IT IS A RELEASE OF LIABILITY. BY SIGNING THIS DOCUMENT, I AM WAIVING ANY RIGHT I OR MY SUCCESSORS MIGHT HAVE TO BRING A LEGAL ACTION OR ASSERT A CLAIM AGAINST WEBFIT FOR YOUR NEGLIGENCE OR THAT OF YOUR EMPLOYEES, AGENTS, OR CONTRACTORS.
          </p>

          <p className="mb-6">
            This form is an important legal document that explains the risks you are assuming by beginning an exercise program. It is critical that you have read and understand this document completely. If you do not understand any part of this document, it is your ultimate responsibility to ask for clarification prior to signing it.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              **Enter First and Last Name
            </label>
            <input
              {...register('name')}
              type="text"
              id="name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter your full name"
              minLength={4}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Continue >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WaiverForm;
